// Focused high-intent landing pages (Phase 2) — one per money keyword.
// conceptKey is used for hreflang pairing with international market pages (Phase 4).

export const FOCUSED_PAGES = [
  {
    slug: "survey-cad-outsourcing",
    keyword: "Survey CAD Outsourcing",
    conceptKey: "outsourcing",
    metaTitle: "Outsourced Survey CAD Drafting Services | GeoDrafts",
    metaDescription:
      "Outsourced survey CAD drafting for land surveying companies — white-label production on your templates, written scope, and QC-checked sheets delivered on schedule.",
    heroLead:
      "Add a production desk without hiring one. GeoDrafts provides outsourced survey CAD drafting and white-label production support for surveying firms that want more capacity without more payroll.",
    badges: ["White-label production", "Your templates & standards", "QC-checked handovers"],
    whatYouGet: [
      "A drafting desk that works your backlog while your crews stay in the field",
      "Fixed written quotes and confirmed delivery schedules before work starts",
      "Drawings on your title blocks, layers, and plot styles — indistinguishable from in-house work",
      "A structured QC pass on every sheet before it reaches your reviewer",
      "Confidential handling of your data and your client relationships",
    ],
    whatWeProduce: [
      "Boundary, ALTA/NSPS, topographic and as-built survey drawings",
      "Subdivision plats, easement exhibits and mortgage location drawings",
      "Utility, route and right-of-way sheets",
      "Point cloud to CAD and PDF to DWG conversions",
      "GIS mapping and data conversion deliverables",
    ],
    howItWorks: [
      { title: "Send data", desc: "Field files, sketches and standards through your secure channel." },
      { title: "Confirm scope", desc: "Fixed quote and schedule agreed in writing." },
      { title: "We draft", desc: "Production on your templates with progress updates." },
      { title: "You certify", desc: "Your licensed professionals review, revise and stamp." },
    ],
    related: ["land-survey-cad-drafting", "topographic-survey-drafting", "boundary-survey-drafting", "point-cloud-to-cad-conversion"],
  },
  {
    slug: "lidar-point-cloud-to-cad",
    keyword: "LiDAR Point Cloud to CAD",
    conceptKey: "point-cloud",
    metaTitle: "LiDAR Point Cloud to CAD Conversion Services | GeoDrafts",
    metaDescription:
      "Point cloud to CAD conversion and LiDAR to CAD drafting — scan-to-CAD production of planimetric drawings, sections and contours from your captured datasets.",
    heroLead:
      "Your scanners capture millions of points — clients still need plans. GeoDrafts converts LiDAR and laser scan datasets into clean, layer-organized CAD deliverables.",
    badges: ["LAS / LAZ / E57 / RCS", "Planimetrics, sections, contours", "Feature-list driven scope"],
    whatYouGet: [
      "Planimetric CAD linework traced from your registered clouds",
      "Building elevations and cross-sections from terrestrial scans",
      "Contour and terrain linework from point data",
      "Organized layers matching your office standard",
      "Coverage QA against the source cloud before delivery",
    ],
    whatWeProduce: [
      "Topographic and existing-conditions plans from scan data",
      "Corridor extraction from mobile mapping datasets",
      "Building facade and interior section drawings",
      "As-built documentation from scan captures",
      "Surface and contour linework for design teams",
    ],
    howItWorks: [
      { title: "Share the dataset", desc: "Decimated exports or agreed tiling for large captures." },
      { title: "Fix the feature list", desc: "Exactly what gets traced, in writing." },
      { title: "We convert", desc: "Traced, layered and annotated to your standard." },
      { title: "Verify & deliver", desc: "Coverage-checked against the cloud, then handed over." },
    ],
    related: ["point-cloud-to-cad-conversion", "drone-point-cloud-drafting", "topographic-survey-drafting", "as-built-survey-drafting"],
  },
  {
    slug: "survey-data-conversion",
    keyword: "Survey Data Conversion",
    conceptKey: "conversion",
    metaTitle: "Survey Data Conversion Services — PDF, Point Files to CAD | GeoDrafts",
    metaDescription:
      "Survey data conversion services: PDF survey to DWG, point file to CAD conversion, raster to vector, and CAD-to-GIS — redrawn and organized to your standards.",
    heroLead:
      "Legacy scans, point files, and GIS exports all need the same thing: clean, structured CAD or GIS deliverables. GeoDrafts converts survey data between formats without losing meaning.",
    badges: ["PDF to DWG redrawing", "Point file normalization", "CAD ↔ GIS both directions"],
    whatYouGet: [
      "PDF and scanned plans redrawn as true, layered CAD geometry",
      "Point files normalized to your coding standard (PNEZD, CSV, TXT)",
      "CAD to GIS conversion with attribution intact",
      "Raster to vector conversion for archives and record modernization",
      "Conversion logs and exception reports — nothing guessed",
    ],
    whatWeProduce: [
      "Redrawn DWG/DXF from PDF and paper plans",
      "Drafting-ready point files and base drawings",
      "Shapefile/GeoJSON deliverables from CAD linework",
      "Parcel and utility datasets with attributes",
      "Batch archive conversions with consistent naming",
    ],
    howItWorks: [
      { title: "Assess sources", desc: "Legibility, scale references and scope confirmed." },
      { title: "Set standards", desc: "Your layers, symbols and schemas configured." },
      { title: "Convert", desc: "Redrawn or transformed with checks at each pass." },
      { title: "Log & deliver", desc: "Comparison plots and exception notes included." },
    ],
    related: ["pdf-to-cad-conversion", "survey-data-processing", "gis-data-conversion", "cadastral-mapping-services"],
  },
  {
    slug: "civil-3d-survey-drafting",
    keyword: "Civil 3D Survey Drafting",
    conceptKey: "civil-3d",
    metaTitle: "Civil 3D Survey Drafting Services | GeoDrafts",
    metaDescription:
      "Civil 3D survey drafting services — surfaces, alignments, plan & profile and grading production on your Civil 3D templates and styles for land surveying companies.",
    heroLead:
      "When your deliverables live in Civil 3D, production needs drafters who speak it. GeoDrafts produces Civil 3D survey drafting on your templates, surfaces and label styles.",
    badges: ["Your Civil 3D templates", "Surfaces & alignments", "Plan production sheets"],
    whatYouGet: [
      "Existing-ground surfaces built from your point files and breaklines",
      "Alignments, profiles and section views per your design direction",
      "Plan and profile sheets on your templates",
      "Grading exhibits and volume documentation from your calculations",
      "LandXML exchange support for survey-to-design workflows",
    ],
    whatWeProduce: [
      "Topographic survey drawings with surface-driven contours",
      "Road and corridor plan-and-profile sheet series",
      "Grading and earthwork exhibit plans",
      "Construction staking plans and coordinate sheets",
      "As-built documentation in your Civil 3D environment",
    ],
    howItWorks: [
      { title: "Share your environment", desc: "Templates, styles and a sample project." },
      { title: "Send field data", desc: "Point files, breaklines and control." },
      { title: "We produce", desc: "Surfaces, sheets and annotation in Civil 3D." },
      { title: "You review", desc: "Your team checks and certifies the deliverables." },
    ],
    related: ["grading-earthwork-drafting", "topographic-survey-drafting", "road-highway-survey-drafting", "construction-staking-drafting"],
  },
];

export const getFocusedPage = (slug) => FOCUSED_PAGES.find((p) => p.slug === slug);
